import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for managing folder state and operations
 */
export function useFolders() {
  const [folders, setFolders] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch all folders for the current user
   */
  const fetchFolders = useCallback(async () => {
    console.log('[useFolders] fetchFolders called');
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      console.log('[useFolders] Token exists:', !!token);

      if (!token) {
        console.warn('[useFolders] No auth token, skipping folder fetch');
        setIsLoading(false);
        return;
      }

      console.log('[useFolders] Fetching /api/folders...');
      const response = await fetch('/api/folders', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('[useFolders] Response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to fetch folders');
      }

      const data = await response.json();
      console.log('[useFolders] Received folders:', data.folders?.length || 0);
      setFolderTree(data.folders || []);

      // Also create flat list for easy lookup
      const flatList = flattenTree(data.folders || []);
      setFolders(flatList);
    } catch (err) {
      console.error('[useFolders] Fetch error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Flatten tree structure to array
   */
  const flattenTree = (tree, result = []) => {
    for (const folder of tree) {
      const { children, ...rest } = folder;
      result.push(rest);
      if (children && children.length > 0) {
        flattenTree(children, result);
      }
    }
    return result;
  };

  /**
   * Fetch folder path (breadcrumb) when currentFolderId changes
   */
  const fetchFolderPath = useCallback(async (folderId) => {
    if (!folderId) {
      setFolderPath([]);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/folders/${folderId}/path`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch folder path');
      }

      const data = await response.json();
      setFolderPath(data.path || []);
    } catch (err) {
      console.error('[useFolders] Fetch path error:', err);
      setFolderPath([]);
    }
  }, []);

  /**
   * Create a new folder
   */
  const createFolder = useCallback(async (name, parentId = null, color = null, icon = null) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/folders', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, parentId, color, icon })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create folder');
      }

      // Refresh folders list
      await fetchFolders();
      return data.folder;
    } catch (err) {
      console.error('[useFolders] Create error:', err);
      throw err;
    }
  }, [fetchFolders]);

  /**
   * Update a folder (name, color, icon)
   */
  const updateFolder = useCallback(async (folderId, updates) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/folders/${folderId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update folder');
      }

      // Refresh folders list
      await fetchFolders();
      return data.folder;
    } catch (err) {
      console.error('[useFolders] Update error:', err);
      throw err;
    }
  }, [fetchFolders]);

  /**
   * Delete a folder
   */
  const deleteFolder = useCallback(async (folderId, transcriptBehavior = 'move_to_parent') => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/folders/${folderId}?transcriptBehavior=${transcriptBehavior}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete folder');
      }

      // If we deleted the current folder, go back to root
      if (currentFolderId === folderId) {
        setCurrentFolderId(null);
        setFolderPath([]);
      }

      // Refresh folders list
      await fetchFolders();
      return true;
    } catch (err) {
      console.error('[useFolders] Delete error:', err);
      throw err;
    }
  }, [fetchFolders, currentFolderId]);

  /**
   * Move a folder to a new parent
   */
  const moveFolder = useCallback(async (folderId, newParentId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/folders/${folderId}/move`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ parentId: newParentId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to move folder');
      }

      // Refresh folders list
      await fetchFolders();
      return data.folder;
    } catch (err) {
      console.error('[useFolders] Move folder error:', err);
      throw err;
    }
  }, [fetchFolders]);

  /**
   * Move transcript(s) to a folder
   */
  const moveTranscriptsToFolder = useCallback(async (transcriptIds, folderId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/transcripts/move', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ transcriptIds, folderId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to move transcripts');
      }

      return data;
    } catch (err) {
      console.error('[useFolders] Move transcripts error:', err);
      throw err;
    }
  }, []);

  /**
   * Bulk delete transcripts
   */
  const bulkDeleteTranscripts = useCallback(async (transcriptIds) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/transcripts/bulk-delete', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ transcriptIds })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete transcripts');
      }

      return data;
    } catch (err) {
      console.error('[useFolders] Bulk delete error:', err);
      throw err;
    }
  }, []);

  /**
   * Reorder folders within a parent
   */
  const reorderFolders = useCallback(async (parentId, orderedIds) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/folders/reorder', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ parentId, orderedIds })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reorder folders');
      }

      // Refresh folders list
      await fetchFolders();
      return true;
    } catch (err) {
      console.error('[useFolders] Reorder error:', err);
      throw err;
    }
  }, [fetchFolders]);

  /**
   * Get a folder by ID from the current state
   */
  const getFolderById = useCallback((folderId) => {
    return folders.find(f => f.id === folderId) || null;
  }, [folders]);

  /**
   * Handle folder selection
   */
  const handleFolderSelect = useCallback((folderId) => {
    setCurrentFolderId(folderId);
    if (folderId) {
      fetchFolderPath(folderId);
    } else {
      setFolderPath([]);
    }
  }, [fetchFolderPath]);

  // Fetch folders on mount
  useEffect(() => {
    console.log('[useFolders] useEffect triggered - calling fetchFolders');
    fetchFolders();
  }, [fetchFolders]);

  // Update folder path when currentFolderId changes
  useEffect(() => {
    if (currentFolderId) {
      fetchFolderPath(currentFolderId);
    }
  }, [currentFolderId, fetchFolderPath]);

  return {
    // State
    folders,
    folderTree,
    currentFolderId,
    folderPath,
    isLoading,
    error,

    // Setters
    setCurrentFolderId: handleFolderSelect,

    // Actions
    fetchFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    moveTranscriptsToFolder,
    bulkDeleteTranscripts,
    reorderFolders,
    getFolderById,

    // Utilities
    clearError: () => setError(null)
  };
}

export default useFolders;
